// ============================================================
// Virtual SEO Tasks
// Synthesize in-memory "tasks" that summarize SEO work
// (project items + Yoast pages) assigned to each user.
// These appear in the main task list alongside real tasks
// but don't exist as rows in the tasks table.
// ============================================================

/**
 * Returns an array of virtual task objects for the current user.
 *
 * @param {string} currentUserId - the user's id
 * @param {object[]} projects - all seo_projects rows with .items joined
 * @param {object[]} yoastPages - all seo_pages rows
 * @param {object[]} clients - clients (for name lookup)
 * @param {string} [currentUserRole] - the user's role (e.g. 'supervising_ad').
 *        Q2 step approvals route to supervising_ad (Lauren).
 */
export function computeVirtualSeoTasks(currentUserId, projects, yoastPages, clients, currentUserRole) {
  if (!currentUserId) return []
  const tasks = []
  const clientById = new Map(clients.map(c => [c.id, c]))

  // Q2 templates whose top-level steps route approval to the supervising AD.
  const Q2_TEMPLATES = ['tpl-old-blogs-aeo-q2-2026', 'tpl-rank-tracker-q2-2026']
  const isSupervisingAd = currentUserRole === 'supervising_ad'

  // ── Project items grouped by project ────────────────────────────────────
  for (const project of projects) {
    // Complete and archived projects never generate to-dos. Archived = closed
    // without finishing (e.g. Q2 2026 SEO projects); the data stays intact.
    if (project.status === 'complete' || project.status === 'archived') continue
    const items = project.items || []
    const client = clientById.get(project.client_id)

    // Q2 projects bundle into ONE task per project (like other SEO projects),
    // visible to everyone in All Tasks and routed to the project's lead assignee.
    // EXCEPTION: blog children (Blog: …) and page items go to different people,
    // so those surface as their own tasks routed to their real assignee.
    // Non-Q2 templates keep the original per-current-user bundled behavior.
    const isQ2 = Q2_TEMPLATES.includes(project.template_id)

    if (isQ2) {
      // Split items: top-level steps (bundled) vs. blog children (separate).
      const isBlogChild = i => /^Blog:\s/.test(i.topic || '')
      const stepItems = items.filter(i => !isBlogChild(i))
      const blogItems = items.filter(isBlogChild)

      // (A1) Bundle the top-level steps, routed to the lead-step assignee.
      const incompleteSteps = stepItems.filter(i => i.status !== 'complete')
      if (incompleteSteps.length > 0) {
        // Lead assignee = assignee of the first step (they all share one person).
        const leadAssignee = stepItems.map(i => i.assigned_to).find(Boolean) || null
        const earliestDue = incompleteSteps.filter(i => i.due_date).map(i => i.due_date).sort()[0]
        tasks.push({
          id: `virt-seo-proj-${project.id}`,
          title: `${project.name} — ${client?.name || 'a client'}`,
          description: `SEO project work for ${client?.name || 'a client'}`,
          status: 'in_progress',
          assigned_to: leadAssignee,
          assigned_by: null,
          client_id: project.client_id,
          service_area: 'seo',
          date_due: earliestDue || null,
          created_at: project.created_at,
          updated_at: project.updated_at,
          is_urgent: false,
          checklist_items: stepItems.map(i => ({
            text: i.topic || i.keyword || '(untitled step)',
            checked: i.status === 'complete',
          })),
          _virtual: true,
          _virtualTarget: { type: 'seo_project', clientId: project.client_id, projectId: project.id },
        })
      }

      // (A2) Blog children — one task each, routed to their own assignee.
      for (const item of blogItems.filter(i => i.status !== 'complete')) {
        tasks.push({
          id: `virt-seo-item-${item.id}`,
          title: `${item.topic || 'Blog'} — ${client?.name || 'a client'}`,
          description: `${project.name} · ${item.notes || ''}`.trim(),
          status: item.status === 'in_progress' ? 'in_progress' : 'not_started',
          assigned_to: item.assigned_to || null,
          assigned_by: null,
          client_id: project.client_id,
          service_area: 'seo',
          date_due: item.due_date || null,
          created_at: item.created_at,
          updated_at: item.updated_at,
          is_urgent: false,
          _virtual: true,
          _virtualTarget: { type: 'seo_project_item', clientId: project.client_id, projectId: project.id, itemId: item.id },
        })
      }
    } else {
      // Non-Q2: one bundled task per assignee on this project, so the work is
      // visible to everyone in All Tasks and filterable by assignee — not just
      // to whoever happens to be logged in. Unassigned items bundle together
      // under a null assignee so they don't disappear.
      const byAssignee = new Map()
      for (const i of items) {
        if (i.status === 'complete') continue
        const key = i.assigned_to || '__unassigned__'
        if (!byAssignee.has(key)) byAssignee.set(key, [])
        byAssignee.get(key).push(i)
      }
      for (const [key, incomplete] of byAssignee.entries()) {
        const assignee = key === '__unassigned__' ? null : key
        const earliestDue = incomplete.filter(i => i.due_date).map(i => i.due_date).sort()[0]
        tasks.push({
          id: `virt-seo-proj-${project.id}-${key}`,
          title: `${incomplete.length} ${incomplete.length === 1 ? 'item' : 'items'} in ${project.name}`
            + `${assignee ? '' : ' (unassigned)'} — ${client?.name || 'a client'}`,
          description: `SEO project work for ${client?.name || 'a client'}`,
          status: 'in_progress',
          assigned_to: assignee,
          assigned_by: null,
          client_id: project.client_id,
          service_area: 'seo',
          date_due: earliestDue || null,
          created_at: project.created_at,
          updated_at: project.updated_at,
          is_urgent: false,
          checklist_items: incomplete.slice(0, 5).map(i => ({
            text: i.topic || i.keyword || '(untitled item)',
            checked: false,
          })),
          _virtual: true,
          _virtualTarget: { type: 'seo_project', clientId: project.client_id, projectId: project.id },
        })
      }
    }

    // (B) Approval queue.
    //   - Q2 projects: top-level steps awaiting approval route to the
    //     supervising AD (Lauren). One approval task per awaiting step.
    //   - Other projects: keep the original behavior (route to the client's AD).
    if (isQ2 && isSupervisingAd) {
      const pendingMyApproval = items.filter(i => i.awaiting_ad_approval && !i.approval_ad && i.status !== 'complete')
      for (const item of pendingMyApproval) {
        tasks.push({
          id: `virt-seo-approve-item-${item.id}`,
          title: `Approve: ${item.topic || item.keyword || 'SEO step'} — ${client?.name || 'a client'}`,
          description: `${project.name} — awaiting your approval`,
          status: 'ready_for_review',
          assigned_to: currentUserId,
          assigned_by: currentUserId, // shows in the Approvals "My Queue"
          client_id: project.client_id,
          service_area: 'seo',
          date_due: item.due_date || null,
          created_at: item.created_at,
          updated_at: item.updated_at,
          is_urgent: false,
          _virtual: true,
          _virtualTarget: { type: 'seo_project_item', clientId: project.client_id, projectId: project.id, itemId: item.id },
        })
      }
    } else if (!isQ2 && client?.account_director_id === currentUserId) {
      const pendingMyApproval = items.filter(i => i.awaiting_ad_approval && !i.approval_ad && i.status !== 'complete')
      if (pendingMyApproval.length > 0) {
        tasks.push({
          id: `virt-seo-approve-${project.id}`,
          title: `${pendingMyApproval.length} SEO ${pendingMyApproval.length === 1 ? 'item needs' : 'items need'} your approval`,
          description: `${project.name} for ${client?.name || 'a client'}`,
          status: 'ready_for_review',
          assigned_to: currentUserId,
          assigned_by: currentUserId,
          client_id: project.client_id,
          service_area: 'seo',
          date_due: null,
          created_at: project.created_at,
          updated_at: project.updated_at,
          is_urgent: false,
          checklist_items: pendingMyApproval.slice(0, 5).map(i => ({
            text: i.topic || i.keyword || '(untitled item)',
            checked: false,
          })),
          _virtual: true,
          _virtualTarget: { type: 'seo_project', clientId: project.client_id, projectId: project.id },
        })
      }
    }
  }

  // ── Yoast pages grouped by client + assignee ────────────────────────────
  // Bundled per client as before, but split by assignee and emitted for every
  // assignee rather than only the current viewer.
  const pagesByKey = new Map()
  for (const page of yoastPages) {
    const fullyDone = page.yoast_rating === 'green' && page.submitted_to_gsc
    if (fullyDone) continue
    const who = page.assigned_to || '__unassigned__'
    const key = `${page.client_id}::${who}`
    if (!pagesByKey.has(key)) pagesByKey.set(key, [])
    pagesByKey.get(key).push(page)
  }
  for (const [key, pages] of pagesByKey.entries()) {
    const [clientId, who] = key.split('::')
    const assignee = who === '__unassigned__' ? null : who
    const client = clientById.get(clientId)
    const earliestDue = pages.filter(p => p.due_date).map(p => p.due_date).sort()[0]
    tasks.push({
      id: `virt-seo-yoast-${clientId}-${who}`,
      title: `${pages.length} Yoast ${pages.length === 1 ? 'page' : 'pages'} to optimize`
        + `${assignee ? '' : ' (unassigned)'} — ${client?.name || 'a client'}`,
      description: `Page-by-page SEO optimization for ${client?.name || 'a client'}`,
      status: 'in_progress',
      assigned_to: assignee,
      assigned_by: null,
      client_id: clientId,
      service_area: 'seo',
      date_due: earliestDue || null,
      created_at: [...pages].sort((a, b) => new Date(a.created_at) - new Date(b.created_at))[0]?.created_at,
      updated_at: [...pages].sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at))[0]?.updated_at,
      is_urgent: false,
      checklist_items: pages.slice(0, 5).map(p => ({
        text: p.page_name,
        checked: p.yoast_rating === 'green',
      })),
      _virtual: true,
      _virtualTarget: { type: 'seo_yoast', clientId },
    })
  }

  return tasks
}